/** SUNTRONIX ONE — ONE SERVICE DESK (internal) + Customer Portal (My Requests).
 *  Renders from the same component via `mode`:
 *   - mode="desk"   → internal Operations desk (tabs, assign, transition, notes)
 *   - mode="portal" → customer "My Requests" (raise + track, portal statuses only)
 *  Backed by /api/service-desk. */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AsyncCombobox } from "@/components/app/AsyncCombobox";
import {
  LifeBuoy, Plus, X, Loader2, MessageSquare, Lock, Send, ChevronRight,
} from "lucide-react";
import NextActionPanel from "@/components/os/NextActionPanel";
import OneFlow from "@/components/oneflow/OneFlow";

const searchTenants = async (q) => {
  const { data } = await api.get("/master-companies", { params: { q, limit: 25 } });
  return (data.items || []).map((c) => ({
    value: c.id, label: c.name || c.legal_name || c.id,
    sub: [c.code, c.country].filter(Boolean).join(" · "),
  }));
};

const CARD_DEFS = [
  ["OPEN", "Open", "text-sky-700 bg-sky-50 border-sky-200"],
  ["AWAITING_CUSTOMER", "Awaiting customer", "text-amber-700 bg-amber-50 border-amber-200"],
  ["AWAITING_SUNTRONIX", "Awaiting Suntronix", "text-indigo-700 bg-indigo-50 border-indigo-200"],
  ["SLA_AT_RISK", "SLA at risk", "text-orange-700 bg-orange-50 border-orange-200"],
  ["BREACHED", "Breached", "text-rose-700 bg-rose-50 border-rose-200"],
  ["RESOLVED", "Resolved", "text-emerald-700 bg-emerald-50 border-emerald-200"],
];

const DESK_TABS = ["OPEN", "MY_TICKETS", "TEAM_TICKETS", "AWAITING_CUSTOMER",
  "AWAITING_SUNTRONIX", "SLA_AT_RISK", "BREACHED", "RESOLVED", "CLOSED"];
const PORTAL_TABS = ["OPEN", "AWAITING_CUSTOMER", "RESOLVED", "CLOSED"];
const PRIORITY_CLS = { P1: "bg-red-100 text-red-700", P2: "bg-orange-100 text-orange-700", P3: "bg-amber-100 text-amber-700", P4: "bg-slate-100 text-slate-600" };

export default function OneServiceDesk({ mode = "desk" }) {
  const portal = mode === "portal";
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("OPEN");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(() => new URLSearchParams(window.location.search).get("new") === "1");
  const [detailId, setDetailId] = useState(null);

  useEffect(() => { api.get("/service-desk/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (portal) {
        const { data } = await api.get("/service-desk/portal/requests", { params: { tab } });
        setItems(data.items || []);
      } else {
        const [l, c] = await Promise.all([
          api.get("/service-desk/tickets", { params: { tab } }),
          api.get("/service-desk/tickets/counts"),
        ]);
        setItems(l.data.items || []);
        setCounts(c.data || {});
      }
    } catch { toast.error("Could not load tickets"); }
    finally { setLoading(false); }
  }, [tab, portal]);
  useEffect(() => { load(); }, [load]);

  const tabs = portal ? PORTAL_TABS : DESK_TABS;

  return (
    <div className="space-y-6" data-testid={portal ? "my-requests" : "service-desk"}>
      <div className="flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: "color-mix(in srgb, var(--st-accent) 12%, transparent)", color: "var(--st-accent)" }}>
          <LifeBuoy className="h-6 w-6" strokeWidth={1.7} />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--st-text)]">{portal ? "My Requests" : "ONE Service Desk"}</h1>
          <p className="text-sm text-[color:var(--st-text-muted)]">{portal ? "Raise a request and track its progress." : "Every ticket linked to its tenant, employee, asset and contract."}</p>
        </div>
        <Button className="ml-auto" data-testid="ticket-new-btn" onClick={() => setOpenNew(true)}>
          <Plus className="h-4 w-4 mr-1.5" /> {portal ? "Raise request" : "New ticket"}
        </Button>
      </div>

      {/* Status command cards */}
      {!portal && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="sd-status-cards">
          {CARD_DEFS.map(([key, label, cls]) => (
            <button key={key} data-testid={`sd-card-${key}`} onClick={() => setTab(key)}
              className={"text-left rounded-xl border p-3 transition-all " + cls +
                (tab === key ? " ring-2 ring-offset-1 ring-current" : " hover:shadow-sm")}>
              <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
              <div className="text-[24px] font-bold tabular-nums">{counts[key] ?? 0}</div>
            </button>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-[color:var(--st-border)] pb-2">
        {tabs.map((t) => (
          <button key={t} data-testid={`ticket-tab-${t}`} onClick={() => setTab(t)}
            className={"px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors " +
              (tab === t ? "bg-[color:var(--st-text)] text-white" : "text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]")}>
            {t.replace(/_/g, " ")}{!portal && counts[t] != null ? ` · ${counts[t]}` : ""}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? <div className="flex items-center gap-2 text-sm text-[color:var(--st-text-subtle)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        : items.length === 0 ? <div className="text-sm text-[color:var(--st-text-muted)]" data-testid="ticket-empty">No tickets in this view.</div>
          : (
            <div className="space-y-2">
              {items.map((t) => (
                <button key={t.id} data-testid={`ticket-row-${t.ticket_number}`} onClick={() => setDetailId(t.id)}
                  className="w-full text-left rounded-xl border border-[color:var(--st-border)] bg-[color:var(--st-surface)] p-4 hover:border-[color:var(--st-accent)] transition-colors flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[color:var(--st-text-subtle)]">{t.ticket_number}</span>
                      {!portal && t.priority && <span className={"text-[10px] font-semibold px-1.5 py-0.5 rounded " + (PRIORITY_CLS[t.priority] || "")}>{t.priority}</span>}
                      <span className="text-[11px] rounded-full bg-[color:var(--st-surface-alt)] px-2 py-0.5 text-[color:var(--st-text-muted)]">{(portal ? t.portal_status : t.status || "").replace(/_/g, " ")}</span>
                      {!portal && t.sla_status === "BREACHED" && <span className="text-[11px] rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 font-semibold text-rose-700" data-testid={`ticket-breached-${t.ticket_number}`}>SLA breached</span>}
                    </div>
                    <div className="mt-1 font-medium text-[color:var(--st-text)] truncate">{t.subject}</div>
                    <div className="text-xs text-[color:var(--st-text-subtle)] truncate">
                      {[t.ticket_type?.replace(/_/g, " "), t.employee_name, t.serial].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[color:var(--st-text-subtle)]" />
                </button>
              ))}
            </div>
          )}

      {openNew && meta && (
        <NewTicketDrawer meta={meta} portal={portal} onClose={() => setOpenNew(false)}
          onCreated={() => { setOpenNew(false); load(); }} />
      )}
      {detailId && meta && (
        <TicketDetail id={detailId} meta={meta} portal={portal}
          onClose={() => setDetailId(null)} onChanged={load} />
      )}
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-[color:var(--st-text-muted)]">{label}{required && <span className="text-red-500"> *</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
const inputCls = "w-full rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[color:var(--st-accent)]";

function NewTicketDrawer({ meta, portal, onClose, onCreated }) {
  const [f, setF] = useState({ ticket_type: "INCIDENT", other_details: "", requester_type: "TENANT", subject: "", description: "", impact: "MEDIUM", urgency: "MEDIUM", serial: "", assigned_team: "", master_company_id: "", subsidiary_id: "", contact_id: "" });
  const [ind, setInd] = useState({ name: "", salutation: "", email: "", mobile: "", alt_phone: "", company: "", address: "", city: "", state: "", pincode: "", country: "" });
  const [busy, setBusy] = useState(false);
  const [assetInfo, setAssetInfo] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [quoteSel, setQuoteSel] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setI = (k, v) => setInd((p) => ({ ...p, [k]: v }));
  const isInd = f.requester_type === "INDIVIDUAL";

  useEffect(() => {
    if (portal) return;
    api.get("/master-companies").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.items || data.companies || []);
      setCompanies(list.map((c) => ({ id: c.id, name: c.name || c.legal_name || c.id })));
    }).catch(() => {});
  }, [portal]);

  // Load subsidiaries + reset dependent fields when tenant changes.
  useEffect(() => {
    if (!f.master_company_id) { setSubs([]); return; }
    api.get("/service-desk/subsidiaries", { params: { tenant: f.master_company_id } }).then(({ data }) => {
      const items = data.items || [];
      setSubs(items);
      if (items.length === 1) set("subsidiary_id", items[0].id);
    }).catch(() => setSubs([]));
  }, [f.master_company_id]);

  const searchContacts = async (q) => {
    if (!f.master_company_id) return;
    const { data } = await api.get("/service-desk/contacts", { params: { tenant: f.master_company_id, q: q || undefined } });
    setContacts(data.items || []);
  };
  const searchQuotes = async (q) => {
    const params = isInd ? { q: q || undefined } : { tenant: f.master_company_id || undefined, q: q || undefined };
    const { data } = await api.get("/service-desk/quotes", { params });
    setQuotes(data.items || []);
  };

  const lookupAsset = async () => {
    if (!f.serial.trim()) return;
    try {
      const { data } = await api.get("/service-desk/asset-context", { params: { serial: f.serial.trim() } });
      setAssetInfo(data);
      if (data.asset?.master_company_id && !f.master_company_id) set("master_company_id", data.asset.master_company_id);
      toast.success(`Asset found: ${data.asset.product || data.asset.serial}`);
    } catch { setAssetInfo(null); toast.message("No matching asset — ticket will be general"); }
  };

  const submit = async () => {
    if (f.ticket_type === "OTHER" && !f.other_details.trim()) { toast.error("Please specify the ticket type/details."); return; }
    if (f.subject.trim().length < 3 || f.description.trim().length < 3) { toast.error("Subject and description are required"); return; }
    if (!portal && !isInd && !f.master_company_id) { toast.error("Select the customer / tenant"); return; }
    if (!portal && isInd && (!ind.name.trim() || !ind.email.trim() || !ind.mobile.trim())) { toast.error("Individual name, email and contact number are required"); return; }
    setBusy(true);
    try {
      const url = portal ? "/service-desk/portal/requests" : "/service-desk/tickets";
      const payload = {
        ticket_type: f.ticket_type, other_details: f.other_details || undefined,
        requester_type: f.requester_type, subject: f.subject, description: f.description,
        impact: f.impact, urgency: f.urgency, assigned_team: f.assigned_team || undefined,
        serial: f.serial || undefined, asset_id: assetInfo?.asset?.asset_id,
        quotation_id: quoteSel?.id, quotation_no: quoteSel?.quotation_number,
      };
      if (isInd) payload.individual = ind;
      else { payload.master_company_id = f.master_company_id; payload.customer_id = f.master_company_id; payload.subsidiary_id = f.subsidiary_id || undefined; payload.contact_id = f.contact_id || undefined; }
      await api.post(url, payload);
      toast.success("Ticket created");
      onCreated();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail) msg = typeof detail === "string" ? detail : "Please check the highlighted fields.";
      else if (e?.response) msg = `Server error creating ticket (HTTP ${e.response.status}). Please retry.`;
      else msg = "Network error — the ticket was not created. Your details are kept; please retry.";
      toast.error(msg);   // form state is intentionally retained for retry
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ticket-new-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[color:var(--st-surface)] h-full overflow-y-auto p-5 space-y-4 shadow-2xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{portal ? "Raise a request" : "New ticket"}</h2>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]"><X className="h-4 w-4" /></button>
        </div>

        <Field label="Type" required>
          <select className={inputCls} value={f.ticket_type} data-testid="ticket-type" onChange={(e) => set("ticket_type", e.target.value)}>
            {meta.ticket_types.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
        </Field>
        {f.ticket_type === "OTHER" && (
          <Field label="Other type / details" required>
            <textarea className={inputCls} rows={2} data-testid="ticket-other-details" value={f.other_details} onChange={(e) => set("other_details", e.target.value)} placeholder="e.g. Device Buyback Query" />
          </Field>
        )}

        {!portal && (
          <Field label="Requester type" required>
            <select className={inputCls} value={f.requester_type} data-testid="ticket-requester-type" onChange={(e) => set("requester_type", e.target.value)}>
              <option value="TENANT">Tenant</option>
              <option value="INDIVIDUAL">Individual</option>
            </select>
          </Field>
        )}

        {!portal && !isInd && (
          <>
            <Field label="Customer / Tenant" required>
              <AsyncCombobox
                value={f.master_company_id}
                selectedLabel={companies.find((c) => c.id === f.master_company_id)?.name}
                onChange={(id, item) => { set("master_company_id", id); set("subsidiary_id", ""); set("contact_id", ""); setContacts([]);
                  if (item?.label && !companies.find((c) => c.id === id)) setCompanies((p) => [...p, { id, name: item.label }]); }}
                fetcher={searchTenants}
                placeholder="Search customer by name or code…" emptyText="No customers found."
                testId="ticket-customer" />
            </Field>
            {subs.length > 0 && (
              <Field label="Subsidiary / Legal entity" required={subs.length > 1}>
                <select className={inputCls} value={f.subsidiary_id} data-testid="ticket-subsidiary" onChange={(e) => set("subsidiary_id", e.target.value)}>
                  <option value="">{subs.length > 1 ? "Select entity…" : "— parent tenant —"}</option>
                  {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
            )}
            {f.master_company_id && (
              <Field label="Contact person">
                <input className={inputCls} data-testid="ticket-contact-search" placeholder="Search name / email / mobile…" onChange={(e) => searchContacts(e.target.value)} onFocus={() => searchContacts("")} />
                {contacts.length > 0 && (
                  <select className={inputCls + " mt-1"} value={f.contact_id} data-testid="ticket-contact" onChange={(e) => set("contact_id", e.target.value)}>
                    <option value="">Select contact…</option>
                    {contacts.map((c) => <option key={c.id} value={c.id}>{[c.name, c.designation, c.email].filter(Boolean).join(" · ")}</option>)}
                  </select>
                )}
              </Field>
            )}
          </>
        )}

        {!portal && isInd && (
          <div className="grid grid-cols-2 gap-3" data-testid="ticket-individual-fields">
            <div className="col-span-2"><Field label="Customer name" required><input className={inputCls} data-testid="ind-name" value={ind.name} onChange={(e) => setI("name", e.target.value)} /></Field></div>
            <Field label="Salutation"><input className={inputCls} data-testid="ind-salutation" value={ind.salutation} onChange={(e) => setI("salutation", e.target.value)} placeholder="Mr / Ms" /></Field>
            <Field label="Email" required><input className={inputCls} data-testid="ind-email" value={ind.email} onChange={(e) => setI("email", e.target.value)} /></Field>
            <Field label="Contact number" required><input className={inputCls} data-testid="ind-mobile" value={ind.mobile} onChange={(e) => setI("mobile", e.target.value)} /></Field>
            <Field label="Alternate contact"><input className={inputCls} data-testid="ind-alt" value={ind.alt_phone} onChange={(e) => setI("alt_phone", e.target.value)} /></Field>
            <div className="col-span-2"><Field label="Company / organisation"><input className={inputCls} data-testid="ind-company" value={ind.company} onChange={(e) => setI("company", e.target.value)} /></Field></div>
            <div className="col-span-2"><Field label="Address"><input className={inputCls} value={ind.address} onChange={(e) => setI("address", e.target.value)} /></Field></div>
            <Field label="City"><input className={inputCls} value={ind.city} onChange={(e) => setI("city", e.target.value)} /></Field>
            <Field label="State"><input className={inputCls} value={ind.state} onChange={(e) => setI("state", e.target.value)} /></Field>
            <Field label="PIN code"><input className={inputCls} value={ind.pincode} onChange={(e) => setI("pincode", e.target.value)} /></Field>
            <Field label="Country"><input className={inputCls} value={ind.country} onChange={(e) => setI("country", e.target.value)} /></Field>
          </div>
        )}

        {!portal && (
          <Field label="Quotation no.">
            <input className={inputCls} data-testid="ticket-quote-search" placeholder="Search quote number / email…" onChange={(e) => searchQuotes(e.target.value)} onFocus={() => searchQuotes("")} />
            {quotes.length > 0 && (
              <select className={inputCls + " mt-1"} value={quoteSel?.id || ""} data-testid="ticket-quote" onChange={(e) => setQuoteSel(quotes.find((q) => q.id === e.target.value) || null)}>
                <option value="">Select quotation…</option>
                {quotes.map((q) => <option key={q.id} value={q.id}>{[q.quotation_number, q.contact_email, q.grand_total].filter(Boolean).join(" · ")}</option>)}
              </select>
            )}
            {quoteSel && <div className="mt-1 text-xs text-[color:var(--st-text-muted)]" data-testid="ticket-quote-context">Linked: {quoteSel.quotation_number}{quoteSel.version ? ` (rev ${quoteSel.version})` : ""}{quoteSel.grand_total ? ` · ${quoteSel.grand_total}` : ""}</div>}
          </Field>
        )}

        <Field label="Subject" required><input className={inputCls} data-testid="ticket-subject" value={f.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Short summary" /></Field>
        <Field label="Description" required><textarea className={inputCls} rows={4} data-testid="ticket-description" value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What is happening?" /></Field>
        <Field label="Asset serial / service tag (optional)">
          <div className="flex gap-2">
            <input className={inputCls} data-testid="ticket-serial" value={f.serial} onChange={(e) => set("serial", e.target.value)} onBlur={lookupAsset} placeholder="e.g. CQX299RMWL" />
            <Button variant="outline" type="button" onClick={lookupAsset} data-testid="ticket-asset-lookup">Find</Button>
          </div>
          {assetInfo && (
            <div className="mt-2 rounded-md bg-[color:var(--st-surface-alt)] p-2 text-xs text-[color:var(--st-text-muted)]" data-testid="ticket-asset-context">
              {assetInfo.asset.product || assetInfo.asset.model} · {assetInfo.asset.serial}
              {assetInfo.employee ? ` · ${assetInfo.employee.name}` : ""}
              {assetInfo.asset.warranty_covered != null ? ` · warranty ${assetInfo.asset.warranty_covered ? "COVERED" : "NOT COVERED"}` : ""}
            </div>
          )}
        </Field>
        {!portal && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Impact"><select className={inputCls} data-testid="ticket-impact" value={f.impact} onChange={(e) => set("impact", e.target.value)}>{meta.impacts.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <Field label="Urgency"><select className={inputCls} data-testid="ticket-urgency" value={f.urgency} onChange={(e) => set("urgency", e.target.value)}>{meta.urgencies.map((x) => <option key={x}>{x}</option>)}</select></Field>
            <div className="col-span-2"><Field label="Assign to queue"><select className={inputCls} data-testid="ticket-queue" value={f.assigned_team} onChange={(e) => set("assigned_team", e.target.value)}><option value="">— unassigned —</option>{meta.queues.map((q) => <option key={q} value={q}>{q.replace(/_/g, " ")}</option>)}</select></Field></div>
          </div>
        )}
        <Button className="w-full" onClick={submit} disabled={busy} data-testid="ticket-submit">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Create ticket</Button>
      </div>
    </div>
  );
}

function TicketDetail({ id, meta, portal, onClose, onChanged }) {
  const [t, setT] = useState(null);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/service-desk/tickets/${id}`).then(({ data }) => setT(data)), [id]);
  useEffect(() => { load(); }, [load]);

  const addComment = async () => {
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/service-desk/tickets/${id}/comments`, { body: comment, type: internal ? "INTERNAL_NOTE" : "CUSTOMER_UPDATE" });
      setComment(""); await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const transition = async (status) => {
    let extra = {};
    if (status === "RESOLVED") {
      const code = window.prompt(`Resolution code (${meta.resolution_codes.slice(0, 6).join(", ")}…):`, "FIXED_REMOTELY");
      const summary = window.prompt("Resolution summary:", "");
      if (!code || !summary) return;
      extra = { resolution_code: code, resolution_summary: summary };
    }
    try { await api.post(`/service-desk/tickets/${id}/transition`, { status, ...extra }); await load(); onChanged?.(); toast.success(`Moved to ${status}`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ticket-detail">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[color:var(--st-surface)] h-full overflow-y-auto p-5 space-y-4 shadow-2xl">
        {!t ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <>
            {!portal && <NextActionPanel kind="ticket" id={id} compact onChanged={() => { load(); onChanged?.(); }} />}
            <OneFlow flowType="SERVICE" recordId={id} audience={portal ? "tenant" : undefined} />
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-[color:var(--st-text-subtle)]">{t.ticket_number}</div>
                <h2 className="text-lg font-semibold text-[color:var(--st-text)]">{t.subject}</h2>
              </div>
              <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md hover:bg-[color:var(--st-surface-alt)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-full bg-[color:var(--st-surface-alt)] px-2 py-0.5">{(portal ? t.portal_status : t.status || "").replace(/_/g, " ")}</span>
              {!portal && t.priority && <span className={"px-2 py-0.5 rounded " + (PRIORITY_CLS[t.priority] || "")}>{t.priority}</span>}
              {t.ticket_type && <span className="rounded-full bg-[color:var(--st-surface-alt)] px-2 py-0.5">{t.ticket_type.replace(/_/g, " ")}</span>}
            </div>
            <p className="text-sm text-[color:var(--st-text)] whitespace-pre-wrap">{t.description}</p>
            {(t.other_details || t.quotation_no || t.requester_type === "INDIVIDUAL" || t.subsidiary_name || t.contact) && (
              <div className="rounded-lg bg-[color:var(--st-surface-alt)] p-3 text-xs text-[color:var(--st-text-muted)] space-y-1" data-testid="ticket-meta">
                {t.ticket_type === "OTHER" && t.other_details && <div><span className="font-medium text-[color:var(--st-text)]">Other type / details:</span> {t.other_details}</div>}
                {t.requester_type === "INDIVIDUAL" && <div><span className="font-medium text-[color:var(--st-text)]">Individual:</span> {t.customer_name} {t.individual?.email ? `· ${t.individual.email}` : ""}</div>}
                {t.subsidiary_name && <div><span className="font-medium text-[color:var(--st-text)]">Subsidiary:</span> {t.subsidiary_name}</div>}
                {t.contact && <div><span className="font-medium text-[color:var(--st-text)]">Contact:</span> {[t.contact.name, t.contact.email].filter(Boolean).join(" · ")}</div>}
                {t.quotation_no && <div><span className="font-medium text-[color:var(--st-text)]">Related quotation:</span> {t.quotation_no}{t.quote?.grand_total ? ` · ${t.quote.grand_total}` : ""}</div>}
              </div>
            )}

            {!portal && (
              <div className="flex flex-wrap gap-2" data-testid="ticket-actions">
                {["TRIAGED", "IN_PROGRESS", "AWAITING_CUSTOMER", "RESOLVED", "CLOSED"].map((s) => (
                  <Button key={s} size="sm" variant="outline" data-testid={`ticket-transition-${s}`} onClick={() => transition(s)}>{s.replace(/_/g, " ")}</Button>
                ))}
              </div>
            )}

            {/* Comments */}
            <div className="space-y-2">
              <div className="text-[12px] font-medium text-[color:var(--st-text-muted)]">Updates</div>
              {(t.comments || []).length === 0 && <div className="text-xs text-[color:var(--st-text-subtle)]">No updates yet.</div>}
              {(t.comments || []).map((c) => (
                <div key={c.id} className={"rounded-lg p-2.5 text-sm " + (c.type === "INTERNAL_NOTE" ? "bg-amber-50 border border-amber-200" : "bg-[color:var(--st-surface-alt)]")}>
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-[color:var(--st-text-subtle)]">
                    {c.type === "INTERNAL_NOTE" ? <><Lock className="h-3 w-3" /> Internal note</> : <><MessageSquare className="h-3 w-3" /> Customer update</>}
                    <span>· {c.author}</span>
                  </div>
                  <div className="mt-1 text-[color:var(--st-text)] whitespace-pre-wrap">{c.body}</div>
                </div>
              ))}
            </div>

            <div className="space-y-2 border-t border-[color:var(--st-border)] pt-3">
              <textarea className={inputCls} rows={2} data-testid="ticket-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add an update…" />
              <div className="flex items-center justify-between">
                {!portal && (
                  <label className="flex items-center gap-1.5 text-xs text-[color:var(--st-text-muted)]">
                    <input type="checkbox" checked={internal} data-testid="ticket-internal-toggle" onChange={(e) => setInternal(e.target.checked)} /> Internal note (not visible to customer)
                  </label>
                )}
                <Button size="sm" onClick={addComment} disabled={busy} data-testid="ticket-comment-send" className="ml-auto"><Send className="h-3.5 w-3.5 mr-1" /> Post</Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
